import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import { blogPosts } from '../mock';
import { ArrowUpRight, Search, Clock } from 'lucide-react';

const allTags = ['All', ...Array.from(new Set(blogPosts.flatMap((p) => [p.tag])))];

const Blog = () => {
  const [q, setQ] = useState('');
  const [tag, setTag] = useState('All');

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return blogPosts.filter((p) => {
      const matchTag = tag === 'All' || p.tag === tag;
      if (!matchTag) return false;
      if (!ql) return true;
      const hay = (p.title + ' ' + p.excerpt + ' ' + (p.tags || []).join(' ')).toLowerCase();
      return hay.includes(ql);
    });
  }, [q, tag]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="font-body bg-paper text-[#1c1c1c]">
      <Navbar />
      <PageHeader
        eyebrow="Wiki & Guides"
        title={<>The Eduvantage <em className="italic">Knowledge Base</em></>}
        description="Deep-dive articles on the Enrolled Agent path — study strategies, career guides, PTIN help, and more. Written by practicing EAs."
        breadcrumb="Blog"
      />

      {/* Search + filters */}
      <section className="pb-4">
        <div className="max-w-6xl mx-auto px-6">
          <div className="card-soft rounded-2xl p-5 md:p-6 flex flex-col md:flex-row items-stretch md:items-center gap-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#3d3d3d]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles, topics, or keywords…"
                className="w-full pl-11 pr-4 py-3 rounded-lg border border-[#143324]/12 bg-white text-[14px] font-body outline-none focus:border-[#143324]/40"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {allTags.map((t) => (
                <button
                  key={t}
                  onClick={() => setTag(t)}
                  className={`px-3 py-1.5 rounded-full text-[12.5px] font-body transition-colors ${
                    tag === t
                      ? 'bg-[#143324] text-[#f5f1e6]'
                      : 'bg-white border border-[#143324]/12 text-[#1c1c1c] hover:border-[#143324]/40'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="py-8 md:py-12">
          <div className="max-w-6xl mx-auto px-6">
            <Link to={`/blog/${featured.slug}`} className="card-soft rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2 group hover:shadow-md transition-shadow">
              <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-7 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4 text-xs text-[#3d3d3d] font-body flex-wrap">
                  <span className="px-2.5 py-1 rounded-full border border-[#143324]/15 text-[#143324]">Featured</span>
                  <span className="px-2.5 py-1 rounded-full border border-[#143324]/15 text-[#143324]">{featured.tag}</span>
                  <span>{featured.date}</span>
                  {featured.readTime && (
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {featured.readTime}</span>
                  )}
                </div>
                <h2 className="font-serif-display text-[#143324] text-3xl md:text-4xl leading-tight mb-4">{featured.title}</h2>
                <p className="text-[#3d3d3d] font-body leading-relaxed text-[14px] mb-6">{featured.excerpt}</p>
                <span className="inline-flex items-center gap-1.5 text-[13px] text-[#143324] font-body group-hover:gap-3 transition-all">
                  Read Full Article <ArrowUpRight size={14} />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-6">
          {rest.length === 0 && filtered.length === 0 ? (
            <div className="card-soft rounded-2xl p-12 text-center">
              <p className="font-body text-[#3d3d3d]">No articles match your search. Try a different keyword.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {rest.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.id} className="card-soft rounded-2xl p-4 md:p-5 group hover:shadow-md transition-shadow">
                  <div className="aspect-[4/3] rounded-xl overflow-hidden mb-5">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="px-2 pb-3">
                    <div className="flex items-center gap-3 mb-3 text-xs text-[#3d3d3d] font-body flex-wrap">
                      <span className="px-2.5 py-1 rounded-full border border-[#143324]/15 text-[#143324]">{post.tag}</span>
                      <span>{post.date}</span>
                    </div>
                    <h3 className="font-serif-display text-[#143324] text-xl md:text-2xl leading-tight mb-3">{post.title}</h3>
                    <p className="text-[#3d3d3d] text-[13px] leading-relaxed font-body mb-4">{post.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 text-[13px] text-[#143324] font-body group-hover:gap-2.5 transition-all">
                      Read More <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
